"use client";

import { useSyncExternalStore } from "react";
import { Toaster, type ToasterProps } from "sonner";

import { useTranslation } from "@/lib/i18n";
import { getResolvedTheme, subscribeTheme } from "@/lib/theme/theme-store";
import type { ResolvedTheme } from "@/lib/theme/resolve-theme";
import { TONE_NOTICE_CLASSES, type Tone } from "@/lib/theme/tone";

/**
 * The application's **single** toast host (Action-Feedback-System §A6,
 * 2026-08-03). Mounted once, in `app/layout.tsx`.
 *
 * ## Why exactly one, and why in the root layout
 *
 * `sonner` keeps its `ToastState` as a **module-level singleton**
 * (`node_modules/sonner/dist/index.mjs:371`) and every `<Toaster>` rendered
 * without an `id` matches every toast that carries no `toasterId`
 * (`:915-926`, `toasts.filter((toast) => !toast.toasterId)`). Two hosts would
 * therefore not partition the stream — they would both render all of it, as two
 * overlapping stacks. So this component must be mounted in exactly one place,
 * and that place has to be above the authenticated shell: while the host lived
 * in `components/providers.tsx` every toast fired from `app/login`,
 * `app/set-password`, `app/select-instance` and `app/auth/*` was a silent no-op
 * (§1.3), which is how the whole pre-auth surface shipped without failure
 * feedback.
 *
 * The one thing the old mount bought — a `key={activeTenant}` remount
 * discarding the previous tenant's toasts — is replaced by an explicit
 * `notify.dismissAll()` on the switch (`components/shell/use-shell-session.ts`).
 *
 * ## Why these props
 *
 * - **`position="bottom-right"`.** sonner pins its container at
 *   `position: fixed; top: 24px` with `z-index: 999999999`
 *   (`sonner/dist/styles.css`), i.e. exactly over the right-hand header cluster
 *   (`components/shell/header/GlobalHeader.tsx:196-234`), which carries
 *   `PageActionsTarget` — the page's own primary actions — plus the tenant
 *   switcher and the notification bell. The shell's notice bars are in-flow flex
 *   siblings (`ShellFrame.tsx:148-156`), so they push the header's content down
 *   while a top-anchored toast stays nailed to 24 px: the collision is certain,
 *   not occasional. Bottom-right has nothing under it.
 * - **`visibleToasts={5}`.** sonner's default cap is 3
 *   (`VISIBLE_TOASTS_AMOUNT`). With three slots occupied, toasts 4..n are
 *   dropped silently.
 * - **`richColors={false}` + `unstyled` toasts.** sonner's rich colours are
 *   hardcoded HSL literals (`index.mjs:404`), invisible to tenant branding, and
 *   its container hardcodes `font-family: ui-sans-serif…`, bypassing the Geist
 *   variable — a direct violation of `.claude/rules/frontend.md`. Its styled
 *   rules are `[data-sonner-toast][data-styled=true]` (specificity 0-2-0) and
 *   its stylesheet is `head.appendChild`-ed at module load, i.e. *after* the app
 *   stylesheet, so a plain Tailwind utility can neither out-specify nor
 *   out-order it. `unstyled: true` drops `data-styled`, which turns that whole
 *   block off and leaves only the positioning/animation rules — the parts worth
 *   keeping. The surface is then painted from {@link TONE_NOTICE_CLASSES}.
 * - **`containerAriaLabel`.** sonner defaults to the English literal
 *   `"Notifications"` (`index.mjs:920`) — a hardcoded user-visible string in a
 *   German-default product. Same for the close button's `"Close toast"`.
 * - **`theme` by client subscription.** Dark mode is `.dark` on `<html>`,
 *   stamped server-side from the `ui-theme` cookie and toggled at runtime with
 *   **no reload** by `lib/theme/theme-store.ts:46-52`. A server-passed theme
 *   would go stale the instant the user touches the appearance switch, so the
 *   value is read through `useSyncExternalStore` — the same shape
 *   `components/settings/profile/AppearanceCard.tsx:27` already uses — with the
 *   server's resolved value as the initial snapshot so there is no flash.
 *
 * Deliberate side effect: importing the theme store here pulls its module-load
 * `init()` (`theme-store.ts:104-106`) onto every pre-auth page. That is wanted —
 * the login screen has no theme today — but it is a behaviour change.
 */

/**
 * The box metrics `TONE_NOTICE_CLASSES` bundles alongside its colours. A toast
 * brings its own padding, radius and border width, so only the colour half of
 * the tone is reused here.
 */
const NOTICE_BOX_CLASSES: ReadonlySet<string> = new Set([
  "rounded",
  "border",
  "px-3",
  "py-2",
  "text-sm",
]);

/**
 * The colour half of a tone: border + text, **without** the `bg-*` wash.
 *
 * The wash is dropped on purpose. `bg-success/10` is 10 % alpha, which is
 * unobjectionable for an inline notice sitting on an opaque page but wrong for
 * a floating overlay — page content would read straight through the toast. The
 * toast is therefore opaque (`bg-popover`) and carries its tone in the border
 * and in the text/icon colour, which is `TONE_TEXT_CLASSES`' contract and is
 * measured against `--card` (= `--popover`) by `lib/theme/tone.test.ts` in both
 * modes across a full hue sweep.
 *
 * Derived rather than transcribed, so an edit in `lib/theme/tone.ts` propagates
 * here. Every utility it can produce is written out literally in `tone.ts`,
 * which is what keeps Tailwind's static extraction able to see them.
 */
export function toneChrome(tone: Tone): string {
  return TONE_NOTICE_CLASSES[tone]
    .split(/\s+/)
    .filter((cls) => cls.length > 0 && !NOTICE_BOX_CLASSES.has(cls) && !cls.startsWith("bg-"))
    .join(" ");
}

/**
 * Per-slot classes for an `unstyled` toast.
 *
 * Note what is *not* here: no colour utility appears both on `toast` and on a
 * per-type key. sonner concatenates `classNames.toast`, `classNames.default` and
 * `classNames[type]` onto the same element, so two competing utilities of the
 * same CSS property would be decided by Tailwind's internal ordering rather than
 * by anything stated here. `bg-popover` is the only colour on the base; border
 * and text colour come exclusively from the per-type keys, and an untyped toast
 * simply inherits `--foreground` and the `*{border-color:--border}` base rule.
 */
export const TOAST_CLASSNAMES = {
  toast:
    "pointer-events-auto flex w-full items-start gap-3 rounded-md border bg-popover px-4 py-3 text-sm shadow-lg "
    + "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
  success: toneChrome("success"),
  error: toneChrome("danger"),
  warning: toneChrome("warning"),
  info: toneChrome("info"),
  loading: toneChrome("neutral"),
  // Children, so these never compete with the tone colour on the <li> itself.
  icon: "mt-0.5 shrink-0 [&>svg]:size-4",
  content: "flex min-w-0 flex-1 flex-col",
  title: "font-medium",
  description: "mt-0.5 text-xs text-muted-foreground",
  closeButton:
    "order-last shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground "
    + "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
  actionButton:
    "shrink-0 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90",
  cancelButton:
    "shrink-0 rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground hover:bg-secondary/80",
} as const satisfies NonNullable<NonNullable<ToasterProps["toastOptions"]>["classNames"]>;

export interface AppToasterProps {
  /**
   * Resolved theme the server rendered with (from the `ui-theme` cookie). Used
   * only as the initial/SSR snapshot — the live value comes from the store.
   */
  initialTheme: ResolvedTheme;
}

export function AppToaster({ initialTheme }: AppToasterProps) {
  const { t } = useTranslation();
  const theme = useSyncExternalStore(
    subscribeTheme,
    getResolvedTheme,
    (): ResolvedTheme => initialTheme,
  );

  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      visibleToasts={5}
      richColors={false}
      closeButton
      containerAriaLabel={t("common.feedback.notificationsLabel")}
      // Inline, because the hardcoded `font-family` sits on sonner's own
      // container rule and an appended stylesheet cannot be beaten by a class.
      style={{ fontFamily: "var(--font-geist-sans)" }}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: t("common.close"),
        classNames: TOAST_CLASSNAMES,
      }}
    />
  );
}
